import React, { useState, KeyboardEvent } from 'react';

interface FormTagInputProps {
  name: string;
  label: string;
  placeholder?: string;
  required?: boolean;
  description?: string;
  value?: string; // Comma-separated string
  onChange?: (value: string) => void;
  error?: string;
  disabled?: boolean;
  className?: string;
}

const FormTagInput: React.FC<FormTagInputProps> = ({
  name,
  label,
  placeholder = "Type and press Enter or comma...",
  required,
  description,
  value = "",
  onChange,
  error,
  disabled,
  className = ""
}) => {
  const [inputValue, setInputValue] = useState("");
  
  const tags = value ? value.split(',').map(tag => tag.trim()).filter(Boolean) : [];

  const addTag = (tag: string) => {
    const trimmedTag = tag.trim();
    if (trimmedTag && !tags.includes(trimmedTag)) {
      const newTags = [...tags, trimmedTag];
      onChange?.(newTags.join(', '));
    }
    setInputValue("");
  };

  const removeTag = (indexToRemove: number) => {
    const newTags = tags.filter((_, index) => index !== indexToRemove);
    onChange?.(newTags.join(', '));
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === ',') {
      e.preventDefault();
      addTag(inputValue);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      removeTag(tags.length - 1);
    }
  };

  return (
    <div className={`flex flex-col ${className}`}>
      <label className="mb-1.5 text-sm font-black text-gray-900 tracking-tight">
        {label} {required && <span className="text-red-500">*</span>}
      </label>
      
      <div className={`flex flex-wrap gap-2 p-2 rounded-xl border transition-all ${
        error ? 'border-red-500' : 'border-slate-200'
      } ${disabled ? 'bg-gray-50' : 'bg-white focus-within:border-blue-500 focus-within:ring-4 focus-within:ring-blue-500/10'}`}>
        {tags.map((tag, index) => (
          <span 
            key={index}
            className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-blue-50 text-blue-700 rounded-lg text-xs font-bold border border-blue-100 animate-in zoom-in-50 duration-200"
          >
            {tag}
            {!disabled && (
              <button
                type="button"
                onClick={() => removeTag(index)}
                className="hover:text-blue-900 focus:outline-none"
              >
                <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            )}
          </span>
        ))}
        
        <input
          type="text"
          className={`flex-1 min-w-[150px] bg-transparent py-1 px-1 text-sm font-medium outline-none placeholder:text-slate-300 ${
            disabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-800'
          }`}
          placeholder={tags.length === 0 ? placeholder : ""}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={() => addTag(inputValue)}
          disabled={disabled}
        />
      </div>
      
      {description && <p className="mt-1.5 text-xs text-gray-400 font-medium leading-relaxed">{description}</p>}
      {error && <p className="mt-1.5 text-xs text-red-500 font-bold">{error}</p>}
    </div>
  );
};

export default FormTagInput;
